/**
 * Dashboard widgets — the registry of sections a user may show or hide on the Dashboard
 * Overview, and the per-user layout that records their choices.
 *
 * Only DEVIATIONS from the defaults are stored (`overrides`), so a widget added later
 * follows its own `defaultEnabled` for everyone until they touch it. Access rules mirror
 * the page each widget comes from (API Credits = admin role + admin.api_credits).
 * For Media Devoted | Created By Ciro Autiero
 */

import type { TrackerId } from '../trackers';

export const DASHBOARD_WIDGET_GROUPS = ['dashboard', 'api-credits'] as const;
export type DashboardWidgetGroup = (typeof DASHBOARD_WIDGET_GROUPS)[number];

export interface DashboardWidgetAccess {
  /** RBAC permission code the user needs to see this widget. */
  permission: string;
  /** Also requires the admin role (same gate as the source page's route). */
  adminOnly?: boolean;
  /** Optional tracker must be configured before the picker shows this widget. */
  tracker?: TrackerId;
}

export interface DashboardWidgetDef<Id extends string = string> {
  id: Id;
  group: DashboardWidgetGroup;
  defaultEnabled: boolean;
  access?: DashboardWidgetAccess;
}

const API_CREDITS_ACCESS: DashboardWidgetAccess = { permission: 'admin.api_credits', adminOnly: true };

export type DashboardWidgetId =
  | 'performance'
  | 'sending'
  | 'countries'
  | 'api-credits.summary'
  | 'api-credits.smswiser'
  | 'api-credits.openrouter'
  | 'api-credits.voluum'
  | 'api-credits.binom'
  | 'api-credits.tres-commas';

/** Registry order is render order on the Overview and list order in the picker. */
export const DASHBOARD_WIDGETS: readonly DashboardWidgetDef<DashboardWidgetId>[] = [
  { id: 'performance', group: 'dashboard', defaultEnabled: true },
  { id: 'sending', group: 'dashboard', defaultEnabled: true },
  { id: 'countries', group: 'dashboard', defaultEnabled: true },
  { id: 'api-credits.summary', group: 'api-credits', defaultEnabled: false, access: API_CREDITS_ACCESS },
  { id: 'api-credits.smswiser', group: 'api-credits', defaultEnabled: false, access: API_CREDITS_ACCESS },
  { id: 'api-credits.openrouter', group: 'api-credits', defaultEnabled: false, access: API_CREDITS_ACCESS },
  { id: 'api-credits.voluum', group: 'api-credits', defaultEnabled: false, access: API_CREDITS_ACCESS },
  { id: 'api-credits.binom', group: 'api-credits', defaultEnabled: false, access: API_CREDITS_ACCESS },
  { id: 'api-credits.tres-commas', group: 'api-credits', defaultEnabled: false, access: { ...API_CREDITS_ACCESS, tracker: 'tres-commas' } },
];

export const DASHBOARD_WIDGET_IDS: readonly DashboardWidgetId[] = DASHBOARD_WIDGETS.map((w) => w.id);

export const DASHBOARD_LAYOUT_VERSION = 1;

/** What is persisted per user: only the widgets whose state differs from the registry default. */
export interface DashboardLayout {
  version: typeof DASHBOARD_LAYOUT_VERSION;
  overrides: Partial<Record<DashboardWidgetId, boolean>>;
}

export const EMPTY_DASHBOARD_LAYOUT: DashboardLayout = { version: DASHBOARD_LAYOUT_VERSION, overrides: {} };

/** Decides whether the current user may use a widget (permission + role). */
export type DashboardWidgetAccessCheck = (access: DashboardWidgetAccess | undefined) => boolean;
